import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocalState, useOnline } from '@ndrc/app-sdk/client';
import { useDesk } from '../desk/context';
import { callCore, mutateCore } from '../lib/rpc';
import { t } from '../messages';
export function MissionResponse({
  runId,
  blockId,
  version,
  kind,
  enabled,
  isolatedChoice = false,
}: {
  runId: string;
  blockId: string;
  version: number;
  kind: 'ack' | 'submit' | 'self_declared';
  enabled: boolean;
  isolatedChoice?: boolean;
}) {
  const { me } = useDesk();
  const online = useOnline();
  const cache = useQueryClient();
  const [draft, setDraft] = useLocalState(
    `mission-response:${me.id}:${runId}:${blockId}${isolatedChoice ? `:${kind}` : ''}`,
    {
      text: '',
      version,
      requestId: crypto.randomUUID(),
      eventId: null as string | null,
      frozen: false,
    },
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const receipt = useQuery({
    queryKey: ['core', 'missions.responseStatus', draft.eventId],
    queryFn: () =>
      callCore<{ status: string; message?: string }>('missions.responseStatus', {
        eventId: draft.eventId,
      }),
    enabled: !!draft.eventId,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.status && query.state.data.status !== 'pending' ? false : 2000,
  });
  const conflict = draft.version !== version;
  async function send() {
    setBusy(true);
    setError('');
    setDraft({ ...draft, frozen: true });
    try {
      const response = await mutateCore<{ eventId: string }>(
        'missions.respond',
        {
          runId,
          blockId,
          expectedVersion: draft.version,
          requestId: draft.requestId,
          kind,
          ...(kind === 'submit' ? { text: draft.text } : {}),
        },
        `mission-response:${draft.requestId}`,
      );
      setDraft({ ...draft, frozen: true, eventId: response.eventId });
      await cache.invalidateQueries({ queryKey: ['core', 'missions.progress', runId] });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('mission.student.error'));
    } finally {
      setBusy(false);
    }
  }
  const label = {
    ack: 'mission.response.ack',
    submit: 'mission.response.send',
    self_declared: 'mission.response.declare',
  } as const;
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void send();
      }}
    >
      {kind === 'submit' && (
        <label className="ndrc-field">
          {t('mission.response.text')}
          <textarea
            className="ndrc-input"
            required
            maxLength={6000}
            disabled={busy || draft.frozen}
            value={draft.text}
            onChange={(event) => setDraft({ ...draft, text: event.target.value })}
          />
        </label>
      )}
      <p className="ndrc-hint">{t('mission.response.local')}</p>
      {error && <p role="alert">{error}</p>}
      {receipt.isError && <p role="alert">{t('mission.student.error')}</p>}
      {draft.eventId && (
        <p role="status">
          {receipt.data?.status === 'ok'
            ? t('mission.response.ok')
            : receipt.data?.status === 'rejected'
              ? receipt.data.message
              : t('mission.response.pending')}
        </p>
      )}
      {conflict && !draft.eventId && <p role="alert">{t('mission.response.conflict')}</p>}
      <button
        className="ndrc-button"
        type="submit"
        disabled={
          !online ||
          !enabled ||
          busy ||
          conflict ||
          !!draft.eventId ||
          (kind === 'submit' && !draft.text.trim())
        }
      >
        {t(label[kind])}
      </button>
      {(conflict || error || receipt.data?.status === 'rejected') &&
        receipt.data?.status !== 'ok' && (
          <button
            className="ndrc-button"
            type="button"
            disabled={busy || !online || receipt.data?.status === 'pending'}
            onClick={() => {
              setDraft({
                ...draft,
                version,
                requestId: crypto.randomUUID(),
                eventId: null,
                frozen: false,
              });
              setError('');
            }}
          >
            {t('mission.response.resume')}
          </button>
        )}
    </form>
  );
}
